import { useId, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Button, Label, RadioGroup, RadioGroupItem } from '@clarix/ui';
import { useSignOut } from '@clarix/layout';
import {
  readOAuthError,
  useApproveOAuthRequest,
  useDenyOAuthRequest,
  useOAuthRequest,
  type OAuthBlockedReason,
  type OAuthGrantAccess,
} from '@/core/api';
import { buildWorkspaceUrl } from '@/gateway/getWorkspaceSlug';

/**
 * `/oauth/consent?req=…` — the member approves (or denies) an AI app's request
 * to act as them in one workspace. Sits under RequireAuth, so login, OTP and
 * 2FA have already run. Spec C3; design notes in docs/MCP-CONNECTOR.md.
 */
export function OAuthConsentPage() {
  const [params] = useSearchParams();
  const reqId = params.get('req');

  const details = useOAuthRequest(reqId);
  const approve = useApproveOAuthRequest();
  const deny = useDenyOAuthRequest();
  const { signOut, signingOut } = useSignOut();
  const [leaving, setLeaving] = useState(false);
  // null until the member picks: until then the app's current access (if connected) shows.
  const [picked, setPicked] = useState<OAuthGrantAccess | null>(null);
  const access: OAuthGrantAccess = picked ?? details.data?.current_access ?? 'read_write';
  const ids = useId();

  const decide = async (mutation: typeof approve) => {
    if (!reqId || leaving) return;
    try {
      const { redirect_url } = await mutation.mutateAsync(
        mutation === approve ? { req: reqId, access } : { req: reqId },
      );
      setLeaving(true);
      window.location.assign(redirect_url);
    } catch {
      /* rendered from mutation.error below */
    }
  };

  const signInTo = async (slug: string) => {
    const next = `/oauth/consent?req=${reqId ?? ''}`;
    const login = `/login?workspace=${encodeURIComponent(slug)}&next=${encodeURIComponent(next)}`;
    await signOut();
    // signOut lands on the OLD workspace's login; this one carries the right slug and `next`.
    window.location.assign(buildWorkspaceUrl(slug, login));
  };

  if (!reqId) return <Expired />;
  if (details.isLoading) {
    return <p className="text-xl text-slate-500">Loading the connection request…</p>;
  }

  if (details.isError || !details.data) {
    const { code, workspaceSlug } = readOAuthError(details.error);
    if (code === 'WORKSPACE_MISMATCH' && workspaceSlug) {
      return (
        <Panel title="Switch workspace to continue">
          <p className="text-xl text-slate-600 dark:text-slate-400">
            This connection is for workspace <strong>{workspaceSlug}</strong>, but you are signed in
            to a different one.
          </p>
          <Button
            type="button"
            className="mt-6 w-full"
            disabled={signingOut}
            onClick={() => void signInTo(workspaceSlug)}
          >
            Sign in to {workspaceSlug}
          </Button>
        </Panel>
      );
    }
    if (code === 'REQUEST_EXPIRED') return <Expired />;
    return (
      <Panel title="Something went wrong">
        <p className="text-xl text-slate-600 dark:text-slate-400">
          We could not load this connection request. Start the connection again from your AI app.
        </p>
      </Panel>
    );
  }

  const view = details.data;
  const actionError = approve.error ?? deny.error;
  const actionCode = actionError ? readOAuthError(actionError).code : null;
  const blocked: OAuthBlockedReason | null =
    view.blocked_reason ??
    (actionCode === 'AI_CONNECTIONS_DISABLED' || actionCode === 'NO_PROJECTS_ACCESS'
      ? actionCode
      : null);
  const busy = approve.isPending || deny.isPending || leaving;

  return (
    <Panel title="Connect an AI app to Clarix">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {view.client_name}
        </span>
        <Badge variant="outline">Unverified app</Badge>
      </div>
      <p className="mt-3 text-xl text-slate-600 dark:text-slate-400">
        wants to use your Clarix tasks, comments and projects as you
        {view.user_email ? ` (${view.user_email})` : ''} in workspace{' '}
        <strong>{view.workspace.name}</strong>. It only ever sees what you can see.
      </p>

      {!blocked && (
        <fieldset className="mt-5" disabled={busy}>
          <legend className="text-xl font-medium text-slate-900 dark:text-slate-100">
            What may it do?
          </legend>
          <RadioGroup
            value={access}
            onValueChange={(v) => setPicked(v === 'read' ? 'read' : 'read_write')}
            className="mt-2 gap-3"
          >
            {ACCESS_ORDER.map((key) => (
              <div key={key} className="flex items-start gap-2">
                <RadioGroupItem id={`${ids}-${key}`} value={key} className="mt-1" />
                <Label htmlFor={`${ids}-${key}`} className="flex flex-col gap-0.5 font-normal">
                  <span className="text-xl font-medium text-slate-900 dark:text-slate-100">
                    {ACCESS_COPY[key].label}
                  </span>
                  <span className="text-lg text-slate-500">{ACCESS_COPY[key].hint}</span>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </fieldset>
      )}
      <p className="mt-2 text-lg text-slate-500">
        The app name is supplied by the app itself. Only continue if you started this connection.
      </p>

      {blocked && (
        <p className="mt-5 rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {BLOCK_COPY[blocked]}
        </p>
      )}
      {actionError && !blocked && (
        <p className="mt-5 rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {actionCode === 'REQUEST_EXPIRED'
            ? 'This request has expired. Start the connection again from your AI app.'
            : actionCode === 'GRANT_LIMIT'
              ? 'You already have the maximum number of connected AI apps. Disconnect one from your profile first.'
              : 'We could not complete that. Please try again.'}
        </p>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3">
        <Button type="button" variant="outline" disabled={busy} onClick={() => void decide(deny)}>
          Deny
        </Button>
        {!blocked && (
          <Button type="button" disabled={busy} onClick={() => void decide(approve)}>
            Allow
          </Button>
        )}
      </div>
      <p className="mt-4 text-center text-lg text-slate-500">
        You&apos;ll be returned to {view.redirect_host}
      </p>
    </Panel>
  );
}

const ACCESS_ORDER: OAuthGrantAccess[] = ['read_write', 'read'];

const ACCESS_COPY: Record<OAuthGrantAccess, { label: string; hint: string }> = {
  read_write: {
    label: 'Read and change',
    hint: 'Look things up, and create or update tasks, change statuses and add comments. Your AI app asks you before each change.',
  },
  read: {
    label: 'Read only',
    hint: 'Look up tasks, comments and projects. It cannot change anything.',
  },
};

const BLOCK_COPY: Record<OAuthBlockedReason, string> = {
  AI_CONNECTIONS_DISABLED:
    'Your workspace admin has turned off AI app connections for this workspace. Ask them to turn it on.',
  NO_PROJECTS_ACCESS:
    "You don't have access to the Projects app in this workspace, so an AI app could not do anything as you. Ask your workspace admin for access.",
};

function Expired() {
  return (
    <Panel title="This request has expired">
      <p className="text-xl text-slate-600 dark:text-slate-400">
        Sign-in requests last 10 minutes and can be used once. Start the connection again from your
        AI app.
      </p>
    </Panel>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <h1 className="mb-4 text-4xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      {children}
    </div>
  );
}
